import { useState, useMemo } from 'react';
import {
  computeLayout,
  getFallbackLayout,
  computeComponentLayout,
  getComponentSheetCount,
  type SheetLayout,
} from '@/components/viewer/drawing/layoutEngine';
import { SHEET } from '@/components/viewer/drawing/constants';
import type { DrawingMetadata } from '@shared/types';

export type DrawingSheetState = {
  sheetNumber: number;
  layout: SheetLayout;
  /** Whether this sheet shows component-level drawings */
  isComponentSheet?: boolean;
};

export function useDrawingSheets(
  metadata: DrawingMetadata | undefined,
  sheetSize: 'A4' | 'A3' | 'A2' | 'A1' = 'A3',
) {
  const [activeSheet, setActiveSheet] = useState(1);

  const sheets = useMemo<DrawingSheetState[]>(() => {
    const sheet = SHEET[sheetSize];
    const margin = SHEET.margin;

    if (!metadata) {
      return [{
        sheetNumber: 1,
        layout: getFallbackLayout(sheet.w, sheet.h, margin),
      }];
    }

    // Component-level drawing mode
    if (metadata.layoutMode === 'components' && metadata.componentDrawings?.length) {
      const componentSheetCount = getComponentSheetCount(metadata.componentDrawings);

      // Sheet 1: Assembly overview (standard 4-view layout)
      const assemblySheet: DrawingSheetState = {
        sheetNumber: 1,
        layout: computeLayout(metadata, 1, sheet.w, sheet.h, margin),
        isComponentSheet: false,
      };

      // Sheets 2+: Component detail drawings
      const componentSheets: DrawingSheetState[] = [];
      for (let i = 0; i < componentSheetCount; i++) {
        componentSheets.push({
          sheetNumber: i + 2,
          layout: computeComponentLayout(
            metadata.componentDrawings,
            sheet.w,
            sheet.h,
            margin,
            i + 1, // 1-indexed for componentLayout's internal pagination
          ),
          isComponentSheet: true,
        });
      }

      return [assemblySheet, ...componentSheets];
    }

    // Standard assembly-level drawing mode
    if (!metadata.sheetAllocation || metadata.sheetAllocation.length === 0) {
      return [{
        sheetNumber: 1,
        layout: computeLayout(metadata, 1, sheet.w, sheet.h, margin),
      }];
    }

    return metadata.sheetAllocation.map((alloc) => ({
      sheetNumber: alloc.sheetNumber,
      layout: computeLayout(metadata, alloc.sheetNumber, sheet.w, sheet.h, margin),
    }));
  }, [metadata, sheetSize]);

  return {
    sheets,
    activeSheet,
    setActiveSheet,
    totalSheets: sheets.length,
  };
}
